import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { Lock, Eye, EyeOff, ArrowLeft, CheckCircle, AlertTriangle, Shield } from 'lucide-react';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [email, setEmail] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    verifyToken();
  }, [token]);

  const verifyToken = async () => {
    if (!token) {
      setVerifying(false);
      setTokenValid(false);
      return;
    }

    try {
      const response = await axios.get(`/api/auth/verify-reset-token/${token}`);
      setTokenValid(response.data.valid);
      setEmail(response.data.email);
    } catch (error) {
      setTokenValid(false);
    } finally {
      setVerifying(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      await axios.post('/api/auth/reset-password', { token, password });
      setSuccess(true);
      toast.success('Password reset successfully!');
      setTimeout(() => navigate('/login'), 3000);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const styles = {
    container: {
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 50%, #0f172a 100%)',
      padding: '20px'
    },
    card: {
      backgroundColor: '#ffffff',
      borderRadius: '20px',
      boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
      width: '100%',
      maxWidth: '440px',
      overflow: 'hidden'
    },
    header: {
      background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)',
      padding: '40px 30px',
      textAlign: 'center'
    },
    errorHeader: {
      background: 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)',
      padding: '40px 30px',
      textAlign: 'center'
    },
    successHeader: {
      background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
      padding: '40px 30px',
      textAlign: 'center'
    },
    iconWrapper: {
      width: '80px',
      height: '80px',
      borderRadius: '50%',
      backgroundColor: 'rgba(255,255,255,0.2)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '0 auto 20px'
    },
    title: {
      color: '#ffffff',
      fontSize: '26px',
      fontWeight: '700',
      margin: '0 0 8px 0'
    },
    subtitle: {
      color: 'rgba(255,255,255,0.85)',
      fontSize: '15px',
      margin: 0
    },
    body: {
      padding: '35px 30px'
    },
    description: {
      color: '#64748b',
      fontSize: '15px',
      lineHeight: 1.7,
      marginBottom: '25px',
      textAlign: 'center'
    },
    formGroup: {
      marginBottom: '20px'
    },
    label: {
      display: 'block',
      marginBottom: '8px',
      fontSize: '14px',
      fontWeight: '600',
      color: '#374151'
    },
    inputWrapper: {
      position: 'relative'
    },
    inputIcon: {
      position: 'absolute',
      left: '14px',
      top: '50%',
      transform: 'translateY(-50%)',
      color: '#9ca3af'
    },
    toggleIcon: {
      position: 'absolute',
      right: '14px',
      top: '50%',
      transform: 'translateY(-50%)',
      color: '#9ca3af',
      cursor: 'pointer',
      background: 'none',
      border: 'none',
      padding: 0
    },
    input: {
      width: '100%',
      padding: '14px 46px',
      border: '2px solid #e5e7eb',
      borderRadius: '12px',
      fontSize: '15px',
      transition: 'all 0.2s',
      outline: 'none',
      boxSizing: 'border-box'
    },
    button: {
      width: '100%',
      padding: '16px',
      background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)',
      color: '#ffffff',
      border: 'none',
      borderRadius: '12px',
      fontSize: '16px',
      fontWeight: '600',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '10px',
      transition: 'all 0.2s'
    },
    backLink: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      marginTop: '25px',
      color: '#6b7280',
      textDecoration: 'none',
      fontSize: '14px',
      fontWeight: '500'
    },
    requirements: {
      backgroundColor: '#f8fafc',
      padding: '16px',
      borderRadius: '10px',
      marginBottom: '20px'
    },
    requirementItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      fontSize: '13px',
      color: '#64748b',
      marginBottom: '6px'
    },
    loadingSpinner: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '60px 20px'
    }
  };

  // Loading state
  if (verifying) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.header}>
            <div style={styles.iconWrapper}>
              <Shield size={40} color="#ffffff" />
            </div>
            <h1 style={styles.title}>Verifying Link</h1>
          </div>
          <div style={styles.loadingSpinner}>
            <div className="spinner" style={{ marginBottom: '20px' }}></div>
            <p style={{ color: '#64748b' }}>Please wait while we verify your reset link...</p>
          </div>
        </div>
      </div>
    );
  }

  // Invalid or expired token
  if (!tokenValid) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.errorHeader}>
            <div style={styles.iconWrapper}>
              <AlertTriangle size={40} color="#ffffff" />
            </div>
            <h1 style={styles.title}>Invalid Link</h1>
            <p style={styles.subtitle}>This reset link is invalid or has expired</p>
          </div>
          <div style={styles.body}>
            <p style={styles.description}>
              The password reset link you clicked is no longer valid. This could happen if:
            </p>
            <ul style={{ color: '#64748b', fontSize: '14px', lineHeight: 1.8, paddingLeft: '20px', marginBottom: '25px' }}>
              <li>The link has expired (links are valid for 1 hour)</li>
              <li>The link has already been used</li>
              <li>The link was copied incorrectly</li>
            </ul>
            <Link to="/forgot-password">
              <button style={styles.button}>
                Request New Reset Link
              </button>
            </Link>
            <Link to="/login" style={styles.backLink}>
              <ArrowLeft size={18} />
              Back to Login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.successHeader}>
            <div style={styles.iconWrapper}>
              <CheckCircle size={40} color="#ffffff" />
            </div>
            <h1 style={styles.title}>Password Reset!</h1>
            <p style={styles.subtitle}>Your password has been changed successfully</p>
          </div>
          <div style={styles.body}>
            <p style={{ ...styles.description, marginBottom: '30px' }}>
              You can now login with your new password. You will be redirected to the login page in a few seconds.
            </p>
            <Link to="/login">
              <button style={styles.button}>
                Go to Login
              </button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Reset password form
  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.header}>
          <div style={styles.iconWrapper}>
            <Lock size={40} color="#ffffff" />
          </div>
          <h1 style={styles.title}>Reset Password</h1>
          <p style={styles.subtitle}>Create a new secure password</p>
        </div>

        <div style={styles.body}>
          {email && (
            <p style={{ ...styles.description, marginBottom: '15px' }}>
              Resetting password for: <strong>{email}</strong>
            </p>
          )}

          <div style={styles.requirements}>
            <div style={styles.requirementItem}>
              <CheckCircle size={16} color={password.length >= 6 ? '#10b981' : '#d1d5db'} />
              At least 6 characters
            </div>
            <div style={styles.requirementItem}>
              <CheckCircle size={16} color={password === confirmPassword && password.length > 0 ? '#10b981' : '#d1d5db'} />
              Passwords match
            </div>
          </div>

          <form onSubmit={handleSubmit}>
            <div style={styles.formGroup}>
              <label style={styles.label}>New Password</label>
              <div style={styles.inputWrapper}>
                <Lock size={20} style={styles.inputIcon} />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter new password"
                  required
                  minLength={6}
                  style={styles.input}
                  onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                  onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                />
                <button
                  type="button"
                  style={styles.toggleIcon}
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Confirm Password</label>
              <div style={styles.inputWrapper}>
                <Lock size={20} style={styles.inputIcon} />
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  required
                  minLength={6}
                  style={styles.input}
                  onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                  onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                />
                <button
                  type="button"
                  style={styles.toggleIcon}
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading || password.length < 6 || password !== confirmPassword}
              style={{
                ...styles.button,
                opacity: loading || password.length < 6 || password !== confirmPassword ? 0.7 : 1,
                cursor: loading || password.length < 6 || password !== confirmPassword ? 'not-allowed' : 'pointer'
              }}
            >
              {loading ? 'Resetting...' : 'Reset Password'}
            </button>
          </form>

          <Link to="/login" style={styles.backLink}>
            <ArrowLeft size={18} />
            Back to Login
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
